import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LANGUAGES } from "@/i18n/languages";

export function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const current = (i18n.language || "en").split("-")[0];
  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = async (code) => {
    setOpen(false);
    if (code === current) return;
    setLoading(true);
    try {
      await i18n.changeLanguage(code);
      // Graceful fallback: if the bundle didn't actually load, i18next uses English.
      if (code !== "en" && !i18n.hasResourceBundle(code, "translation")) {
        toast.info("That language isn't fully available yet — showing English where needed.");
      }
    } catch {
      toast.error("Couldn't load that language. Staying on the current one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors duration-300"
        data-testid="language-switcher-toggle"
        aria-label={t("lang.choose")}
        disabled={loading}
      >
        {loading ? <Loader2 size={15} className="animate-spin text-[#00F0FF]" /> : <Globe size={15} />}
        {!compact && <span className="max-w-[6.5rem] truncate">{active.native}</span>}
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 rtl:right-auto rtl:left-0 mt-3 w-52 max-h-80 overflow-y-auto rounded-2xl bg-black/90 backdrop-blur-2xl border border-white/10 shadow-2xl p-1.5 z-[60]"
            data-testid="language-switcher-menu"
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => pick(l.code)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-sm transition-colors duration-200 ${
                  l.code === current ? "bg-[#0055FF]/20 text-white" : "text-slate-300 hover:bg-white/5"
                }`}
                data-testid={`lang-option-${l.code}`}
              >
                <span className="flex flex-col leading-tight">
                  <span className="font-medium">{l.native}</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">{l.name}</span>
                </span>
                {l.code === current && <Check size={14} className="text-[#00F0FF] shrink-0" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
